<template>
  <div class="LocatieInformatie__Content">
    <div v-if="!isLoading">
      <template v-if="address">
        <p>
          <strong>Adres</strong><br/>
          {{ address.simple_address }} <br/>
          {{ address.postalcode }} {{ address.city }}
        </p>

        <p v-if="address.coordinates['lat'] && address.coordinates['lng']">
          <strong>Coordinaten</strong><br/>
          {{ address.coordinates['lat'] }} <br/>
          {{ address.coordinates['lng'] }}
        </p>
      </template>
      <p v-else>
        <strong>Adres</strong><br/>
        Onbekend
      </p>

      <p v-if="plannerName">
        <strong>Gebruiker</strong><br/>
        {{ plannerName }}
      </p>

      <p v-if="prio && prio.order">
        <strong>Prioriteit</strong><br/>
        {{ prio.order }}
        {{ prio.fase === 1 ? '(Fase 1)' : '' }}
        {{ isBraLiProvincieCode && prio.fase === 2 ? '(Fase 2)' : '' }}
      </p>

      <Form @submit="handleSubmitUpdate">
        <FormField
          v-model="fields.remark.value"
          v-bind="fields.remark" />
        <FormField
          v-model="fields.status.value"
          v-bind="fields.status"
          :disabled="! isAllowedToEditStatus"
          :options="statusOptions" />
        <FormField
          v-if="isLocatievoorstel"
          v-model="fields.cpo.value"
          v-bind="fields.cpo"
          :options="cpoSelectOptions" />

        <p>
          <strong>Locatiebeheer</strong>
        </p>
        <template v-if="superuser">
          <FormField
            v-model="fields.beheerder.value"
            v-bind="fields.beheerder"
            :options="beheerderOptions" />
          <div class="d-flex align-items-center add-validator">
            <FormField
              v-model="fields.validatie.value"
              v-bind="fields.validatie"
              :options="usersByCodeAsOptions" />
            <b-icon-plus
              variant="dark"
              class="add-validator__icon u-clickable ml-2 mt-1"
              @click="$_chargingpointValidatorsMixin_addValidator"
            />
          </div>
          <div class="the-validators">
            <ul v-if="newValidators.length > 0">
              <li
                v-for="(validator, index) in newValidators"
                :key="validator.user_id"
                class="mb-1 d-flex"
              >
                {{ validator.email }}
                <b-icon-trash
                  variant="dark"
                  class="the-validators__validator__icon u-clickable pull-right"
                  @click="$_chargingpointValidatorsMixin_removeValidator(index)"
              />
              </li>
            </ul>
            <p v-else class="text-muted">
              Geen validatoren geselecteerd
            </p>
          </div>
        </template>
        <template v-else>
          <p>
            <strong>Beheerder</strong><br />
            {{ beheerder ? beheerder.name : 'Geen' }}
          </p>
          <div class="the-validators">
            <p class="mb-0">
              <strong>Validatoren</strong>
            </p>
            <ul v-if="hasValidators">
              <li
                v-for="validator in validators"
                :key="validator.user_id"
              >
                {{ validator.name }}
              </li>
            </ul>
            <span v-else>
              Geen
            </span>
          </div>
        </template>

        <div class="mt-2 d-flex justify-content-end">
          <b-button
            v-if="canRemove"
            class="mr-1"
            size="sm"
            variant="danger"
            @click.prevent="handleRemove"
          >
            Verwijder
          </b-button>
          <b-button
            type="submit"
            size="sm"
            variant="primary">
          Opslaan
        </b-button>
        </div>
      </Form>
    </div>
    <p v-else class="text-muted">
      Loading...
    </p>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import {BIconPlus, BIconTrash} from "bootstrap-vue";

import Form from '@/components/form/Form'
import FormField from '@/components/form/FormField'

import {reduceUserInformation} from "@/helpers/user";

import chargingpointDataMixin from "@/mixins/chargingpoint/chargingpointDataMixin";
import chargingpointEditMixin from "@/mixins/chargingpoint/chargingpointEditMixin";
import chargingpointCpoMixin from "@/mixins/chargingpoint/chargingpointCpoMixin";
import chargingpointValidatorsMixin from "@/mixins/chargingpoint/chargingpointValidatorsMixin";
import userMixin from "@/mixins/common/userMixin";

export default {
  components: {Form, FormField, BIconPlus, BIconTrash},
  mixins: [chargingpointDataMixin, chargingpointEditMixin, chargingpointCpoMixin, chargingpointValidatorsMixin, userMixin],
  data() {
    return {
      fields: {
        remark: {
          label: 'Toelichting',
          value: this.remark,
          type: 'textarea'
        },
        status: {
          label: 'Status',
          value: 'suggestion',
          type: 'select'
        },
        beheerder: {
          label: 'Beheerder',
          value: null,
          type: 'select'
        },
        validatie: {
          label: 'Validatie',
          value: null,
          type: 'select'
        },
      },
    }
  },
  computed: {
    ...mapGetters('planmode', {
      chargingpoint: 'getSelectedChargingpoint',
      isPlanModeActive: 'isActive',
    }),
    ...mapGetters('access', [
      'getActiveMunicipality'
    ]),
    canRemove() {
      // return this.getEditMode && (isPlanModeAdmin() || (isAnalist() && (this.userName === this.currentUserName) && (this.status.value !== 'definitive')))
      return true // TODO: Add permission check - Role can differ per municipality!
    },
    isAllowedToEditStatus() {
      // superuser always are allowed to change the status
      if (this.superuser) {
        return true
      }

      // if there is no beheerder, everyone may change the status
      if (this.chargingpoint.data.properties.beheerder === undefined) {
        return true
      }

      // is user the beheerder?
      return this.currentUserId === this.chargingpoint.data.properties.beheerder.user_id
    },
  },
  created: async function(){
    /* check what users may do/don't */
    this.init()

    /* connect to pusher */
    await this.$_chargingpointEditMixin_connectToPusher()
  },
  destroyed() {
    this.fields.status.class = ''
  },
  methods: {
    /* check what the user is eligible to do */
    init() {
      let point = this.chargingpoint

      this.fields.status.value = point.data.properties.status || (this.isPlanModeAdmin ? 'definitive' : 'suggestion')
      this.fields.cpo.value = this.currentCpo ? this.currentCpo.uuid : ''
      this.fields.beheerder.value = point.data.properties.beheerder?.user_id || null
      this.fields.remark.value = point.data.properties.remark || ''
      this.newValidators = point.data.properties.validators || []
    },

    /**
     * Update the marker
     */
    async handleSubmitUpdate() {
      let point = this.chargingpoint

      // if there was an cpo already assigned, filter it out and add the (if) selected one
      const stakeholders = this.$_chargingpointCpoMixin_removeOrUpdateCpoFromStakeholders()

      let beheerder = this.usersByCode.find(user => user.user_id === this.fields.beheerder.value)
      if (beheerder !== undefined) {
        beheerder = reduceUserInformation(beheerder)
      }

      try {
        await this.$_chargingpointEditMixin_save({
          code: this.getActiveMunicipality,
          ref: point.ref, // full ref
          id: point.data.properties.id,
          stakeholders: stakeholders,
          status: this.fields.status.value,
          user: {
            name: point.data.properties.user_name || point.data.properties.user.name,
          },
          coordinates: point.data.coordinates,
          remark: this.fields.remark.value,
          geocoder: false,
          validators: this.newValidators,
          beheerder: beheerder || null,
        })
      } catch (error) {
        console.warn('ERROR: ', error)
      }
    },

    /**
     * Remove the marker from the data set
     */
    handleRemove() {
      this.$bvModal.msgBoxConfirm(
        'Bevestig het verwijderen van dit punt.', {
          okVariant: 'danger',
          okTitle: 'Bevestigen',
          cancelTitle: 'Annuleren'
        })
        .then(confirmed => {
          if (confirmed) {

            /* this.delete broadcasts to close the popup/sidepanel */
            this.$_chargingpointEditMixin_delete({
              code: this.getActiveMunicipality,
              ref: this.chargingpoint.ref // full ref
            })
          }
        })
    },
  }
}
</script>

<style lang="scss">
@import "./src/assets/sass/config";

.FormField {
  label, legend {
    font-size: 1rem;
    font-weight: 600;
    text-transform: none;
    color: map_get($theme-colors, 'primary')
  }
}

.ActionSidebar {
  .add-validator {
    .custom-select {
      max-width: 196px;
    }
  }
}

.add-validator {
  width: calc(100% - 26px) !important;

  &__icon {
    font-size: 25px;
  }
}

.the-validators {
  ul {
    list-style: none;
    padding: 0;
  }
}
</style>